define([
    'lodash',
    'coreUtils',
    'santa-components',
    'santaProps/types/modules/ServiceTopologySantaTypes',
    'santaProps/types/modules/BrowserFlagsSantaTypes'
], function (
    _,
    coreUtils,
    santaComponents,
    ServiceTopologySantaTypes,
    BrowserFlagsSantaTypes
) {
    'use strict';

    const consts = coreUtils.mediaConsts;
    const maximumVideoQuality = {
        quality: '1080p',
        width: 1920,
        height: 1080
    };

    const LEGACY_FULL_WIDTH_CONTAINERS = {
        'wysiwyg.viewer.components.StripContainer': true,
        'wysiwyg.viewer.components.StripContainerSlideShow': true,
        'wysiwyg.viewer.components.StripColumnsContainer': true,
        'mobile.core.components.Page': true,
        'wysiwyg.viewer.components.SiteBackground': true,
        'SITE_BACKGROUND': true,
        'wysiwyg.viewer.components.Column'(state, props) {
            if (isMobile(state, props)) {
                return true;
            }
            const compId = props.structure.id;
            const pointers = state.siteAPI.getPointers();
            const displayedDAL = state.siteAPI.getDisplayedDAL();
            const pagePointer = pointers.components.getPage(props.rootId, state.siteData.getViewMode());
            const compPointer = pointers.components.getComponent(compId, pagePointer);

            const isSingleColumn = pointers.components.getSiblings(compPointer).length === 0;
            if (!isSingleColumn) {
                return false;
            }
            const columnsContainer = pointers.components.getParent(compPointer);
            const propertyQuery = displayedDAL.get(pointers.getInnerPointer(columnsContainer, ['propertyQuery']));
            const propertyItem = pointers.data.getPropertyItem(propertyQuery, props.rootId);
            const isFullWidth = displayedDAL.get(pointers.getInnerPointer(propertyItem, ['fullWidth']));

            return isFullWidth;
        }
    };

    function isMobile(state, props) {
        const isMobileView = state.fetchSantaType(santaComponents.santaTypesDefinitions.isMobileView, state, props);
        const isMobileDevice = state.fetchSantaType(santaComponents.santaTypesDefinitions.Device.isMobileDevice, state, props);
        return isMobileView || isMobileDevice;
    }

    function isLegacyFullWidthContainer(state, props) {
        const compType = _.get(props, ['structure', 'componentType']) || props.id;
        const isFullWidth = LEGACY_FULL_WIDTH_CONTAINERS[compType];

        if (_.isFunction(isFullWidth)) {
            return isFullWidth(state, props);
        }

        return !!isFullWidth;
    }


    function getVideoQualityByLayout(mediaData, layout) {
        const qualities = _.reject(mediaData.qualities, {quality: 'storyboard'});
        const quality = qualities.reverse().reduce(function (acc, item) {
            const qualityWidth = parseInt(item.width, 10);
            const qualityHeight = parseInt(item.height, 10);
            const isQualityBigger = qualityWidth > layout.width && qualityHeight > layout.height;
            if (isQualityBigger) {
                return item.quality;
            }
            return parseInt(acc, 10) ? acc : item.quality;
        }, '0');
        return parseInt(maximumVideoQuality.quality, 10) < parseInt(quality, 10) ? maximumVideoQuality.quality : quality;
    }

    function getCurrentQualityState(data, state, key) {
        const mediaData = _.get(data, ['background', 'mediaRef']);
        if (isVideoInData(mediaData)) {
            const id = key || mediaData.videoId;
            return _.get(state.siteAPI.getAspectGlobalData('mediaAspect'), id) || {};
        }
        return {};
    }

    function getLayout(state, props) {
        if (isLegacyFullWidthContainer(state, props)) {
            //return state.siteData.getScreenSize();
            if (props.id === 'SITE_BACKGROUND') {
                return maximumVideoQuality;
            }
            const compLayout = state.fetchSantaType(santaComponents.santaTypesDefinitions.Component.style, state, props);
            return {
                width: maximumVideoQuality.width,
                height: _.get(compLayout, 'height') || maximumVideoQuality.height
            };
        }
        return state.fetchSantaType(santaComponents.santaTypesDefinitions.Component.style, state, props);
    }


    function getMediaQuality(data, state, props) {
        const mediaData = _.get(data, ['background', 'mediaRef']) || {};
        if (isVideoInData(mediaData)) {
            return getVideoQualityByLayout(mediaData, getLayout(state, props));
        }
        return '';
    }

    function isVideoInData(mediaData) {
        return _.get(mediaData, 'type') === 'WixVideo';
    }

    function getPlaybackConfig(data, state, props) {
        const mediaData = _.get(data, ['background', 'mediaRef']);
        if (isVideoInData(mediaData) && _.find(mediaData.adaptiveVideo, {format: 'hls'})) {
            const qualities = _.reject(mediaData.qualities, {quality: 'storyboard'});
            const targetQuality = getVideoQualityByLayout(mediaData, getLayout(state, props));
            const targetQualityIndex = _.findIndex(qualities, ['quality', targetQuality]);

            return {
                hls: {
                    debug: state.siteData.isQaMode(),
                    startLevel: targetQualityIndex,
                    capLevelToPlayerSize: true
                }
            };
        }
        return {};
    }

    function getUrl(data, state, props) {
        const mediaData = _.get(data, ['background', 'mediaRef']);
        if (isVideoInData(mediaData)) {
            const playbackFormat = getPlaybackFormat(data, state, props);
            if (playbackFormat === 'hls') {
                const targetFormat = _.find(mediaData.adaptiveVideo, {format: 'hls'});
                return coreUtils.urlUtils.joinURL(ServiceTopologySantaTypes.adaptiveVideoDomain.fetch(state, props), _.get(targetFormat, 'url'));
                //return 'http://files-test.wix.com/files/video/5d38f6_15b514b5ec3a4e4893b60299ee160d03/repackage/hls';
            } else if (playbackFormat === 'mp4') {
                // the quality that we want to play
                const preferredQuality = getMediaQuality(data, state, props);
                const qualityState = getCurrentQualityState(data, state);
                // the qualities that are ready
                const readyQualities = _.get(qualityState, 'readyQualities');
                let playbackQuality;
                if (_.isEmpty(readyQualities) || _.includes(readyQualities, preferredQuality)) {
                    playbackQuality = preferredQuality;
                } else {
                    playbackQuality = _.last(readyQualities);
                }
                const targetQuality = _.find(mediaData.qualities, {quality: playbackQuality});
                //prefer video url from new design data structure
                if (targetQuality.url) {
                    return coreUtils.urlUtils.joinURL(ServiceTopologySantaTypes.staticVideoUrl.fetch(state, props), targetQuality.url);
                }
                //build uri from videoId
                return coreUtils.urlUtils.joinURL(ServiceTopologySantaTypes.staticVideoUrl.fetch(state, props), mediaData.videoId, preferredQuality, 'mp4', 'file.mp4');
            }
        }
        return '';
    }

    function isApplicableForAdaptive(mediaData/*, state, props*/) {
        const hlsItem = _.find(mediaData.adaptiveVideo, {format: 'hls'});
        const aboveMinDuration = parseInt(mediaData.duration, 10) > 60;
        const isTransparentVideo = _.includes(mediaData.mediaFeatures, 'alpha');
        return !isTransparentVideo && hlsItem && aboveMinDuration;
    }

    function getPlaybackFormat(data, state, props) {
        const mediaData = _.get(data, ['background', 'mediaRef']);
        if (isVideoInData(mediaData) && mediaData.qualities) {
            const mediaQualityState = getCurrentQualityState(data, state, mediaData.videoId);
            const isMP4Fallback = _.get(mediaQualityState, 'fallback');
            if (!isMP4Fallback && isApplicableForAdaptive(mediaData, state, props)) {
                return 'hls';
            }
            return 'mp4';
        }
        return '';
    }

    function isVideoEnabledForMobile(state, props) {
        const isMobileView = state.fetchSantaType(santaComponents.santaTypesDefinitions.isMobileView, state, props);
        const isDesktopDevice = !state.fetchSantaType(santaComponents.santaTypesDefinitions.Device.isTouchDevice, state, props);
        const isTabletDevice = state.siteData.isTabletDevice();
        const isSupportBgVideoDevice = isDesktopDevice || (isTabletDevice && state.siteAPI.isExperimentOpen('sv_enableBackgroundVideoOnTablet'));

        if (isSupportBgVideoDevice && !isMobileView) {
            return true;
        }
        const compProp = state.fetchSantaType(santaComponents.santaTypesDefinitions.Component.compProp, state, props);
        const isDisabled = _.get(compProp, 'enableBackgroundVideo') === false;
        return !isDisabled;
    }

    function shouldRenderVideo(mediaData, state, props) {
        const hasVideoAndEnabled = isVideoInData(mediaData) && isVideoEnabledForMobile(state, props);

        if (hasVideoAndEnabled) {
            const isTransparentVideo = _.includes(mediaData.mediaFeatures, 'alpha');

            if (isTransparentVideo) {
                return BrowserFlagsSantaTypes.webglCrossOriginSupported.fetch(state, props)();
            }

            return true;
        }

        return false;
    }

    function getVideoRenderParts(data, state, props) {
        let parts = [];
        const mediaData = _.get(data, ['background', 'mediaRef']);
        if (shouldRenderVideo(mediaData, state, props)) {
            let shouldRenderVideoPart = true;
            const qualityState = getCurrentQualityState(data, state, mediaData.videoId);
            const videoErrors = _.get(qualityState, [consts.eventTypes.ERROR]);
            const hasNoVideoError = videoErrors === consts.errorTypes.NO_VIDEO_FOUND;
            if (hasNoVideoError) {
                shouldRenderVideoPart = false;
            } else {
                const playbackFormat = getPlaybackFormat(data, state, props);
                const isHlsFormat = playbackFormat === 'hls';
                const adaptiveQualityState = getCurrentQualityState(data, state, 'adaptiveVideo');
                const isHlsLoaded = _.get(adaptiveQualityState, ['hlsLoaded']);
                if (!playbackFormat || (isHlsFormat && !isHlsLoaded)) {
                    shouldRenderVideoPart = false;
                }
            }
            parts = shouldRenderVideoPart ? ['poster', 'video'] : ['poster'];
        }
        return parts;
    }


    return {
        getPlaybackFormat,
        getMediaQuality,
        getUrl,
        getPlaybackConfig,
        getVideoRenderParts
    };
});
